import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getEasternDateString } from '../lib/utils';
import { logger } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LocalStorage keys
const DAILY_PLAYED_KEY = 'jigsaw_daily_played';
const DAILY_SCORE_KEY = 'jigsaw_daily_score';
const DAILY_PROMPT_SHOWN_KEY = 'jigsaw_daily_prompt_shown';
const DAILY_PLAYER_NAME_KEY = 'jigsaw_daily_player_name';

/**
 * Hook to manage daily challenge state and logic.
 * Handles localStorage persistence, device checks, and streak tracking.
 * 
 * @param {string} visitorId - Device fingerprint ID
 * @param {number} currentGridSize - Current grid size (4 for mobile, 6 for desktop)
 * @param {boolean} isFingerprintLoading - Whether fingerprint is still loading
 */
export function useDailyChallenge(visitorId, currentGridSize, isFingerprintLoading) {
  const [isDailyMode, setIsDailyMode] = useState(false);
  const [dailyPuzzleDate, setDailyPuzzleDate] = useState(null);
  const [hasPlayedDailyToday, setHasPlayedDailyToday] = useState(false);
  const [todayDailyScore, setTodayDailyScore] = useState(null);
  const [playerStreak, setPlayerStreak] = useState(null);
  const [deviceCheckDone, setDeviceCheckDone] = useState(false);

  // Fetch player streak info
  const fetchPlayerStreak = useCallback(async (playerName) => {
    if (!playerName) return;
    try {
      const response = await axios.get(`${API}/daily/streak/${encodeURIComponent(playerName)}`);
      setPlayerStreak(response.data);
    } catch (error) {
      logger.error('Failed to fetch player streak:', error);
    }
  }, []);

  // Check device status with server when fingerprint is ready
  useEffect(() => {
    const checkDeviceStatus = async () => {
      if (!visitorId || isFingerprintLoading || deviceCheckDone) return;
      
      try {
        const isMobile = currentGridSize === 4;
        const response = await axios.post(`${API}/daily/check-device?is_mobile=${isMobile}`, {
          device_id: visitorId
        });
        
        if (response.data.has_played) {
          setHasPlayedDailyToday(true);
          setTodayDailyScore(response.data.score);
          const today = getEasternDateString();
          localStorage.setItem(DAILY_PLAYED_KEY, today);
          localStorage.setItem(DAILY_SCORE_KEY, JSON.stringify(response.data.score));
        } else {
          // Server says not played - clear any stale localStorage data
          // This handles the case where an admin deleted the entry
          setHasPlayedDailyToday(false);
          setTodayDailyScore(null);
          localStorage.removeItem(DAILY_PLAYED_KEY);
          localStorage.removeItem(DAILY_SCORE_KEY);
        }
        setDeviceCheckDone(true);
      } catch (error) {
        logger.error('Failed to check device status:', error);
        setDeviceCheckDone(true);
      }
    };
    
    checkDeviceStatus();
  }, [visitorId, isFingerprintLoading, deviceCheckDone, currentGridSize]);

  // Check daily challenge status on mount (localStorage fallback)
  useEffect(() => {
    const today = getEasternDateString();
    const playedDate = localStorage.getItem(DAILY_PLAYED_KEY);
    const savedScore = localStorage.getItem(DAILY_SCORE_KEY);
    const savedPlayerName = localStorage.getItem(DAILY_PLAYER_NAME_KEY);
    
    if (playedDate === today && savedScore) {
      setHasPlayedDailyToday(true);
      const parsedScore = JSON.parse(savedScore);
      setTodayDailyScore(parsedScore);
      if (savedPlayerName) {
        fetchPlayerStreak(savedPlayerName);
      }
    } else if (playedDate !== today) {
      // New day - reset localStorage
      setHasPlayedDailyToday(false);
      setTodayDailyScore(null);
      localStorage.removeItem(DAILY_PLAYED_KEY);
      localStorage.removeItem(DAILY_SCORE_KEY);
      localStorage.removeItem(DAILY_PROMPT_SHOWN_KEY);
      if (savedPlayerName) {
        fetchPlayerStreak(savedPlayerName);
      }
    }
  }, [fetchPlayerStreak]);

  // Check if daily prompt should be shown
  const shouldShowDailyPrompt = useCallback((pendingChallengeCode, showChallengeInfo, isChallengeMode) => {
    const today = getEasternDateString();
    const promptShown = localStorage.getItem(DAILY_PROMPT_SHOWN_KEY);
    const playedDate = localStorage.getItem(DAILY_PLAYED_KEY);
    
    // Skip if in challenge mode or already shown/played
    if (pendingChallengeCode || showChallengeInfo || isChallengeMode) return false;
    if (promptShown === today || playedDate === today) return false;
    
    return true;
  }, []);

  // Mark daily prompt as shown
  const markDailyPromptShown = useCallback(() => {
    const today = getEasternDateString();
    localStorage.setItem(DAILY_PROMPT_SHOWN_KEY, today);
  }, []);

  // Start daily challenge
  const startDailyChallenge = useCallback((imageUrl, puzzleDate) => {
    setIsDailyMode(true);
    setDailyPuzzleDate(puzzleDate);
    return { imageUrl, puzzleDate };
  }, []);

  // Handle saving daily score
  const saveDailyScore = useCallback((savedScore) => {
    const today = getEasternDateString();
    localStorage.setItem(DAILY_PLAYED_KEY, today);
    localStorage.setItem(DAILY_SCORE_KEY, JSON.stringify(savedScore));
    
    if (savedScore.player_name) {
      localStorage.setItem(DAILY_PLAYER_NAME_KEY, savedScore.player_name);
    }
    
    setHasPlayedDailyToday(true);
    setTodayDailyScore(savedScore);
    
    // Update streak info from saved score
    if (savedScore.streak) {
      setPlayerStreak({
        current_streak: savedScore.streak.current,
        longest_streak: savedScore.streak.longest,
        badges: savedScore.streak.badges,
        next_badge: savedScore.streak.next_badge,
        streak_active: true
      });
    }
    
    setIsDailyMode(false);
  }, []);

  // Exit daily mode
  const exitDailyMode = useCallback(() => {
    setIsDailyMode(false);
    setDailyPuzzleDate(null);
  }, []);

  // Force re-check device status (useful when admin deletes entry)
  const recheckDeviceStatus = useCallback(() => {
    setDeviceCheckDone(false);
  }, []);

  return {
    // State
    isDailyMode,
    dailyPuzzleDate,
    hasPlayedDailyToday,
    todayDailyScore,
    playerStreak,
    
    // Actions
    startDailyChallenge,
    saveDailyScore,
    exitDailyMode,
    shouldShowDailyPrompt,
    markDailyPromptShown,
    fetchPlayerStreak,
    recheckDeviceStatus
  };
}
